import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTemplatesViewModel } from '@viewModels/TemplatesViewModel/TemplatesViewModel';
import { BasicLoader } from '@shared/Ui/Loaders/BasicLoader/BasicLoader';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { SanitizeHTML } from '@shared/Helpers/SanitizeHTML';
import { TemplateBlockContent } from '@modules/BillOfMaterials/Components/TemplateInfo/TemplateBlockContent/TemplateBlockContent';
import { EmptyTemplateContent } from '@modules/BillOfMaterials/Components/TemplateInfo/TemplateBlockContent/EmptyTemplateContent/EmptyTemplateContent';
import styles from './TemplateInfo.module.css';
import { ROUTES } from '@configs/AppRoutes';
import { useAuthorizationViewModel } from '@viewModels/AuthorizationViewModel/AuthorizationViewModel';
import { useMediaType } from '@shared/Hooks/useMediaTypeHook';
import { DesktopLandingGifsSection } from '@shared/Features/DesktopLandingGifsSection/DesktopLandingGifsSection';
import { MobileLandingGifsSection } from '@shared/Features/MobileLandingGifsSection/MobileLandingGifsSection';
import { CustomProjectInfoViewModel } from '@viewModels/CustomProjectInfoViewModel';
import {
    notifyErrorCustom,
    notifySuccessCustom,
    notifyWarningCustom,
} from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { TemplateInfoButtons } from '@modules/BillOfMaterials/Components/TemplateInfo/TemplateInfoButtons/TemplateInfoButtons';
import { TemplateInfoHeader } from '@modules/BillOfMaterials/Components/TemplateInfo/TemplateInfoHeader/TemplateInfoHeader';
import { useProjectStore } from '../../../../Stores/ProjectStore';

const AUTH_PAGE_URL = import.meta.env.VITE_BASE_AUTH_PAGE_CASHBUILD;

export const TemplateInfo: React.FC = () => {
    const { tempId } = useParams();

    const navigate = useNavigate();
    const {
        templateDescription,
        currentTemplate,
        setShouldRedirectToNewProjectAfterAuthorization,
    } = useTemplatesViewModel();

    const { isProjectLoading } = useProjectStore();
    const projectViewModel = new CustomProjectInfoViewModel();

    const { bearerToken } = useAuthorizationViewModel();

    const [isDesktop, isTablet, _isMobile] = useMediaType();

    const handleBuyTemplate = async () => {
        if ((!bearerToken || bearerToken === '') && tempId) {
            const backPath = `${window.location.pathname}#${ROUTES.TEMPLATE_CART.getPath(tempId)}`;
            const encodedBack = encodeURIComponent(backPath);

            window.location.replace(`${AUTH_PAGE_URL}?back=${encodedBack}`);
            if (!currentTemplate?.paid) {
                setShouldRedirectToNewProjectAfterAuthorization(true);
            }
            return;
        }

        if (currentTemplate?.paid && tempId) {
            navigate(ROUTES.TEMPLATE_CART.getPath(tempId));
            return;
        }

        notifyWarningCustom('Preparing template. Please wait...');
        const success = await projectViewModel.createNewProjectWithFreeTemplate(
            [Number(tempId)]
        );

        if (success.error) {
            notifyErrorCustom(
                success.message ?? 'Failed to add template to new project'
            );
            return;
        }

        const projectId = success.data?.id ?? 0;

        navigate(
            ROUTES.STEP1.getPath(StepsViewModeEnum.PROJECT, String(projectId))
        );

        notifySuccessCustom('Successfully added template to new project!');
    };

    // const handleViewProjectExample = () => {
    //     navigate(ROUTES.STEP1.getPath(StepsViewModeEnum.PRESENTATION));
    // };

    if (!templateDescription) {
        return (
            <>
                <TemplateInfoHeader isTitleRequired={true} />
                <div className={styles.noContent}>No content available</div>
                {isDesktop || isTablet ? (
                    <DesktopLandingGifsSection />
                ) : (
                    <MobileLandingGifsSection />
                )}
                <TemplateInfoButtons
                    onBuyTemplate={handleBuyTemplate}
                    // onViewProjectExample={handleViewProjectExample}
                />
            </>
        );
    }

    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(templateDescription, 'text/html');
    const isNewFormat = parsedHtml.querySelector('[data-template-format="v2"]');

    const blocks = parsedHtml.querySelectorAll('.image-block');

    const sanitizedHtml = SanitizeHTML(templateDescription);

    if (blocks.length === 0 && !isNewFormat) {
        return <EmptyTemplateContent description={sanitizedHtml} />;
    }

    return (
        <section className={styles.section}>
            <TemplateInfoHeader isTitleRequired={!isNewFormat} />
            {templateDescription ? (
                <>
                    {isNewFormat ? (
                        <section
                            dangerouslySetInnerHTML={{
                                __html: templateDescription,
                            }}
                            className={styles.newContent}
                        />
                    ) : (
                        <>
                            {Array.from(blocks).map((block, idx) => (
                                <TemplateBlockContent
                                    key={idx}
                                    block={block}
                                    index={idx}
                                />
                            ))}
                        </>
                    )}
                    {isDesktop || isTablet ? (
                        <DesktopLandingGifsSection />
                    ) : (
                        <MobileLandingGifsSection />
                    )}
                </>
            ) : (
                <div className={styles.noContent}>No content available</div>
            )}
            <TemplateInfoButtons
                onBuyTemplate={handleBuyTemplate}
                isLoading={isProjectLoading}
                // onViewProjectExample={handleViewProjectExample}
            />
        </section>
    );
};
